import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import WhatsAppButton from "../components/buttons/whats-app-button";
import ShareButton from "../components/buttons/share-button";
import Header from "../components/Header";
import Footer from "../components/Footer";

const FacturacionElectronica = () => {
  const tiposEcf = [
    { codigo: "31", nombre: "Factura de Crédito Fiscal", descripcion: "Para ventas a empresas con RNC. Permite crédito fiscal al comprador.", icon: "bx-buildings", color: "primary", textColor: "text-white" },
    { codigo: "32", nombre: "Factura de Consumo", descripcion: "Para ventas al consumidor final. La más común en colmados y retail.", icon: "bx-user", color: "success", textColor: "text-white" },
    { codigo: "33", nombre: "Nota de Débito", descripcion: "Ajuste de facturas emitidas por cargos adicionales no facturados.", icon: "bx-up-arrow-alt", color: "warning", textColor: "text-dark" },
    { codigo: "34", nombre: "Nota de Crédito", descripcion: "Devoluciones, descuentos o correcciones de facturas anteriores.", icon: "bx-down-arrow-alt", color: "info", textColor: "text-dark" },
    { codigo: "41", nombre: "Compras", descripcion: "Respaldo de compras a proveedores informales para deducción fiscal.", icon: "bx-cart", color: "danger", textColor: "text-white" },
    { codigo: "43", nombre: "Gastos Menores", descripcion: "Para egresos de caja chica por montos menores.", icon: "bx-receipt", color: "secondary", textColor: "text-white-dark" },
    { codigo: "44", nombre: "Regímenes Especiales Electrónico", descripcion: "Comprobante utilizado en transacciones bajo regímenes especiales con fines de deducción, acreditamiento o crédito fiscal.", icon: "bx-receipt", color: "primary", textColor: "text-white-dark" },
    { codigo: "45", nombre: "Gubernamental Electrónico", descripcion: "Comprobante utilizado en transacciones realizadas por entidades gubernamentales.", icon: "bx-buildings", color: "light", textColor: "text-dark" },
    { codigo: "46", nombre: "Exportación", descripcion: "Comprobante utilizado en transacciones de exportación.", icon: "bx-receipt", color: "success", textColor: "text-white-dark" },
  ];

  const pasos = [
    {
      num: "01",
      icon: "bx-id-card",
      titulo: "Solicita tu firma digital",
      desc: "Obtén tu certificado digital ante la DGII. ProVenta te guía en todo el proceso de registro.",
    },
    {
      num: "02",
      icon: "bx-cog",
      titulo: "Configura en ProVenta",
      desc: "Activa el módulo e-CF desde los ajustes de tu empresa en ProVenta con un solo clic.",
    },
    {
      num: "03",
      icon: "bx-file",
      titulo: "Factura normalmente",
      desc: "El sistema firma y envía el comprobante a la DGII de forma automática en tiempo real.",
    },
    {
      num: "04",
      icon: "bx-check-shield",
      titulo: "La DGII confirma",
      desc: "Recibes la aprobación instantánea. Tu cliente obtiene su e-CF válido por correo o WhatsApp.",
    },
  ];

  const faqs = [
    {
      q: "¿Qué es la Facturación Electrónica (e-CF)?",
      a: "Es el sistema de comprobantes fiscales electrónicos implementado por la DGII en República Dominicana. Reemplaza los NCF físicos por documentos digitales firmados electrónicamente, con mayor seguridad y trazabilidad.",
    },
    {
      q: "¿Cuándo es obligatorio?",
      a: "La DGII ha establecido plazos por categorías de contribuyentes. Las empresas del Régimen Ordinario ya deben cumplir. Verifica tu categoría en el portal de la DGII para conocer tu fecha límite.",
    },
    {
      q: "¿Tiene costo adicional para clientes de ProVenta?",
      a: "No. La Facturación Electrónica está incluida sin costo adicional para todos los suscriptores activos de ProVenta. Solo necesitas tu firma digital de la DGII.",
    },
    {
      q: "¿Necesito internet para facturar?",
      a: "Sí, para el envío del e-CF a la DGII se requiere conexión a internet. Sin embargo, ProVenta puede facturar offline y sincronizar los comprobantes en cuanto se restablezca la conexión.",
    },
    {
      q: "¿Qué pasa con mis NCF físicos actuales?",
      a: "Durante la transición puedes usar NCF físicos y e-CF simultáneamente. ProVenta administra ambos tipos sin complicaciones hasta que completes la migración total.",
    },
  ];

  return (
    <React.Fragment>
      <Helmet>
        <title>Facturación Electrónica e-CF con la DGII | ProVenta República Dominicana</title>
        <meta
          name="description"
          content="ProVenta integra Facturación Electrónica (e-CF) con la DGII. Emite comprobantes fiscales electrónicos automáticamente, sin costo adicional para suscriptores. Cumple la ley fácilmente."
        />
        <meta
          name="keywords"
          content="facturación electrónica República Dominicana, e-CF DGII, comprobantes fiscales electrónicos, NCF electrónico, software facturación electrónica RD"
        />
        <link rel="canonical" href="https://www.proventa.app/funcionalidades/facturacion-electronica/" />
      </Helmet>

      <div className="bg-dark text-light min-vh-100 d-flex flex-column">
        <Header />

        {/* ── HERO ── */}
        <section
          className="position-relative overflow-hidden py-5"
          style={{
            marginTop: "80px",
            background:
              "radial-gradient(ellipse at 20% 50%, rgba(99,102,241,0.18) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(139,92,246,0.15) 0%, transparent 55%), #0b0f19",
          }}
        >
          {/* Blobs */}
          <div
            className="position-absolute rounded-circle"
            style={{ width: 400, height: 400, top: -120, right: -80, background: "rgba(99,102,241,0.12)", filter: "blur(90px)", zIndex: 0 }}
          />
          <div
            className="position-absolute rounded-circle"
            style={{ width: 260, height: 260, bottom: -60, left: "10%", background: "rgba(139,92,246,0.1)", filter: "blur(70px)", zIndex: 0 }}
          />

          <div className="container position-relative" style={{ zIndex: 2 }}>
            <div className="row align-items-center gy-5">
              {/* Left */}
              <div className="col-lg-6 text-center text-lg-start">
                <div className="d-inline-flex align-items-center gap-2 mb-4">
                  <span
                    className="badge px-3 py-2 rounded-pill fw-bold text-uppercase"
                    style={{ background: "linear-gradient(135deg,#ffd600,#ff9800)", color: "#1a1a1a", fontSize: "0.7rem", letterSpacing: "0.08em" }}
                  >
                    ¡Incluido Gratis!
                  </span>
                  <span className="text-light opacity-60 fs-sm">Para todos los suscriptores activos</span>
                </div>

                <h1 className="display-4 text-white fw-bold mb-4" style={{ lineHeight: 1.15 }}>
                  Facturación Electrónica{" "}
                  <span
                    style={{
                      background: "linear-gradient(135deg,#818cf8,#a78bfa)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    (e‑CF)
                  </span>{" "}
                  integrada con la DGII
                </h1>

                <p className="fs-lg text-light opacity-75 mb-5" style={{ maxWidth: 520 }}>
                  Emite comprobantes fiscales electrónicos directamente desde ProVenta. El sistema firma, envía y registra cada factura ante la DGII de forma automática — sin complicaciones, sin costo adicional.
                </p>

                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-start">
                  <a
                    href="/"
                    onClick={(e) => e.preventDefault()}
                    className="btn btn-lg px-5 py-3 fw-bold rounded-pill text-white"
                    style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)", boxShadow: "0 8px 30px rgba(99,102,241,0.4)" }}
                  >
                    <i className="bx bx-download me-2 fs-5" />
                    Activar en mi cuenta
                  </a>
                  <a
                    href="https://wa.me/18097874963"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-lg btn-outline-light border-opacity-25 px-4 py-3 rounded-pill"
                  >
                    <i className="bx bxl-whatsapp me-2 fs-5 text-success" />
                    Hablar con soporte
                  </a>
                </div>

                {/* Trust badges */}
                <div className="mt-5 d-flex flex-wrap justify-content-center justify-content-lg-start gap-4">
                  {[
                    { icon: "bx-check-shield", label: "Certificado DGII" },
                    { icon: "bx-lock-alt", label: "Firma Digital" },
                    { icon: "bx-time", label: "Envío en tiempo real" },
                  ].map((b) => (
                    <div key={b.label} className="d-flex align-items-center gap-2 opacity-70">
                      <i className={`bx ${b.icon} text-primary fs-5`} />
                      <span className="fs-sm text-light">{b.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right — visual */}
              <div className="col-lg-6 d-flex justify-content-center">
                <div
                  className="rounded-4 p-4 position-relative"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    backdropFilter: "blur(12px)",
                    maxWidth: 460,
                    width: "100%",
                  }}
                >
                  {/* Mock e-CF card */}
                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="rounded-3 d-flex align-items-center justify-content-center"
                        style={{ width: 48, height: 48, background: "linear-gradient(135deg,#6366f1,#8b5cf6)" }}
                      >
                        <i className="bx bx-receipt text-white fs-4" />
                      </div>
                      <div>
                        <div className="text-white fw-bold">e-CF Generado</div>
                        <div className="fs-xs opacity-50">Comprobante Electrónico</div>
                      </div>
                    </div>
                    <span
                      className="badge px-3 py-2 rounded-pill fw-bold"
                      style={{ background: "rgba(34,197,94,0.15)", color: "#4ade80", border: "1px solid rgba(34,197,94,0.3)", fontSize: "0.7rem" }}
                    >
                      ✓ DGII Aprobado
                    </span>
                  </div>

                  <div className="mb-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)", paddingBottom: 16 }}>
                    {[
                      ["Tipo", "Factura de Crédito Fiscal (31)"],
                      ["RNC Emisor", "1-32-12345-6"],
                      ["Secuencia", "E310000000001"],
                      ["Monto", "RD$ 12,500.00"],
                      ["ITBIS (18%)", "RD$ 2,250.00"],
                    ].map(([k, v]) => (
                      <div key={k} className="d-flex justify-content-between py-1">
                        <span className="fs-sm opacity-50">{k}</span>
                        <span className="fs-sm text-light fw-medium">{v}</span>
                      </div>
                    ))}
                  </div>

                  <div className="d-flex gap-2">
                    <button className="btn btn-sm flex-fill rounded-pill" style={{ background: "rgba(99,102,241,0.2)", color: "#818cf8", border: "1px solid rgba(99,102,241,0.3)", fontSize: "0.8rem" }}>
                      <i className="bx bx-envelope me-1" /> Enviar por email
                    </button>
                    <button className="btn btn-sm flex-fill rounded-pill" style={{ background: "rgba(34,197,94,0.1)", color: "#4ade80", border: "1px solid rgba(34,197,94,0.25)", fontSize: "0.8rem" }}>
                      <i className="bx bxl-whatsapp me-1" /> WhatsApp
                    </button>
                  </div>

                  {/* Floating pulse indicator */}
                  <div
                    className="position-absolute d-flex align-items-center gap-2"
                    style={{ bottom: -16, right: 20, background: "#0b0f19", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 999, padding: "6px 14px" }}
                  >
                    <span
                      style={{
                        width: 8, height: 8, borderRadius: "50%",
                        background: "#4ade80",
                        boxShadow: "0 0 8px #4ade80",
                        animation: "pulseDotEcf 1.5s infinite",
                        display: "inline-block",
                      }}
                    />
                    <span className="fs-xs text-light opacity-60">Conectado a la DGII</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <style>{`
            @keyframes pulseDotEcf {
              0%,100% { opacity:1; transform:scale(1); }
              50%      { opacity:.4; transform:scale(1.5); }
            }
          `}</style>
        </section>

        {/* ── BENEFICIOS ── */}
        <section className="container py-5 my-lg-3">
          <div className="text-center mb-5">
            <span className="badge bg-primary bg-opacity-15 text-white border border-primary border-opacity-20 px-3 py-2 rounded-pill mb-3 fw-bold" style={{ fontSize: "0.75rem" }}>
              VENTAJAS
            </span>
            <h2 className="display-5 text-white mb-3">¿Por qué usar e‑CF con ProVenta?</h2>
            <p className="fs-lg text-light opacity-60" style={{ maxWidth: 520, margin: "0 auto" }}>
              Más seguridad, menos papeleo y total cumplimiento fiscal desde el primer día.
            </p>
          </div>

          <div className="row g-4">
            {[
              { icon: "bx-check-shield", color: "#6366f1", title: "100% cumplimiento DGII", desc: "Tu empresa siempre al día con la normativa fiscal vigente. Evita multas y sanciones." },
              { icon: "bx-time-five", color: "#f59e0b", title: "Automático y en tiempo real", desc: "Cada factura se firma y envía a la DGII en segundos. Sin pasos manuales ni intermediarios." },
              { icon: "bx-dollar-circle", color: "#10b981", title: "Sin costo adicional", desc: "Incluido en todos los planes activos de ProVenta. No pagas extra por la funcionalidad." },
              { icon: "bx-history", color: "#8b5cf6", title: "Historial centralizado", desc: "Consulta todos tus e-CF emitidos, anulados y pendientes desde un solo panel." },
              { icon: "bx-paper-plane", color: "#0ea5e9", title: "Entrega instantánea", desc: "Tu cliente recibe el e-CF por email o WhatsApp al instante, con PDF adjunto." },
              { icon: "bx-transfer", color: "#f43f5e", title: "NCF y e-CF simultáneos", desc: "Usa ambos formatos durante la transición sin perder ninguna venta ni historial." },
            ].map((b) => (
              <div key={b.title} className="col-md-6 col-lg-4">
                <div
                  className="h-100 p-4 rounded-4"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", transition: "border-color 0.25s" }}
                >
                  <div
                    className="d-inline-flex align-items-center justify-content-center rounded-3 mb-3"
                    style={{ width: 52, height: 52, background: `${b.color}22` }}
                  >
                    <i className={`bx ${b.icon} fs-3`} style={{ color: b.color }} />
                  </div>
                  <h3 className="h5 text-white mb-2">{b.title}</h3>
                  <p className="fs-sm text-light opacity-60 mb-0">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── TIPOS DE e-CF ── */}
        <section className="py-5" style={{ background: "rgba(255,255,255,0.02)", borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="container">
            <div className="text-center mb-5">
              <span className="badge bg-success bg-opacity-15 text-dark border border-success border-opacity-20 px-3 py-2 rounded-pill mb-3 fw-bold" style={{ fontSize: "0.75rem" }}>
                TIPOS DE COMPROBANTE
              </span>
              <h2 className="display-5 text-white mb-3">Todos los e‑CF soportados</h2>
              <p className="text-light opacity-60 fs-lg" style={{ maxWidth: 480, margin: "0 auto" }}>
                ProVenta maneja todos los tipos de comprobantes fiscales electrónicos definidos por la DGII.
              </p>
            </div>

            <div className="row g-3">
              {tiposEcf.map((t) => (
                <div key={t.codigo} className="col-md-6 col-lg-4">
                  <div
                    className="p-3 rounded-4 d-flex gap-3 align-items-start h-100"
                    style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                  >
                    <div
                      className={`d-flex align-items-center justify-content-center rounded-3 flex-shrink-0 bg-opacity-15`}
                      style={{ width: 44, height: 44 }}
                    >
                      <i className={`bx ${t.icon} text-${t.color} fs-4`} />
                    </div>
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span
                          className={`badge bg-${t.color} bg-opacity-20 ${t.textColor}`}
                          style={{ fontSize: "0.65rem", fontWeight: 700 }}
                        >
                          Tipo {t.codigo}
                        </span>
                      </div>
                      <h4 className="h6 text-white mb-1">{t.nombre}</h4>
                      <p className="fs-xs text-light opacity-55 mb-0">{t.descripcion}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CÓMO FUNCIONA ── */}
        <section className="container py-5 my-lg-3">
          <div className="text-center mb-5">
            <span
              className="badge px-3 py-2 rounded-pill mb-3 fw-bold"
              style={{ fontSize: "0.75rem", background: "rgba(245,158,11,0.15)", color: "#fbbf24", border: "1px solid rgba(245,158,11,0.25)" }}
            >
              PROCESO
            </span>
            <h2 className="display-5 text-white mb-3">¿Cómo empezar?</h2>
            <p className="text-light opacity-60 fs-lg" style={{ maxWidth: 480, margin: "0 auto" }}>
              En 4 simples pasos tu empresa empieza a emitir e-CF de forma legal y automática.
            </p>
          </div>

          <div className="row g-4 justify-content-center">
            {pasos.map((p, idx) => (
              <div key={p.num} className="col-md-6 col-lg-3">
                <div className="text-center position-relative">
                  {/* Connector line (desktop) */}
                  {idx < pasos.length - 1 && (
                    <div
                      className="d-none d-lg-block position-absolute"
                      style={{
                        top: 28, left: "calc(50% + 36px)", width: "calc(100% - 36px)",
                        height: 2,
                        background: "linear-gradient(90deg,rgba(99,102,241,0.5),rgba(139,92,246,0.2))",
                        zIndex: 0,
                      }}
                    />
                  )}
                  <div
                    className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4 position-relative"
                    style={{
                      width: 58, height: 58,
                      background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                      boxShadow: "0 8px 24px rgba(99,102,241,0.35)",
                      zIndex: 1,
                    }}
                  >
                    <i className={`bx ${p.icon} text-white fs-3`} />
                  </div>
                  <div
                    className="position-absolute text-white fw-black"
                    style={{ top: -8, left: "50%", transform: "translateX(-60%) translateY(-50%)", fontSize: "3.5rem", opacity: 0.04, fontWeight: 900, lineHeight: 1, userSelect: "none" }}
                  >
                    {p.num}
                  </div>
                  <h3 className="h5 text-white mb-2">{p.titulo}</h3>
                  <p className="fs-sm text-light opacity-60 mb-0">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── ACOMPAÑAMIENTO DE CERTIFICACIÓN ── */}
        <section
          className="py-5"
          style={{
            background: "linear-gradient(135deg,rgba(99,102,241,0.11),rgba(139,92,246,0.04))",
            borderTop: "1px solid rgba(129,140,248,0.14)",
            borderBottom: "1px solid rgba(129,140,248,0.14)",
          }}
        >
          <div className="container py-lg-4">
            <div className="row align-items-center g-5">
              <div className="col-lg-6">
                <span
                  className="badge px-3 py-2 rounded-pill mb-3 fw-bold"
                  style={{ fontSize: "0.75rem", background: "rgba(129,140,248,0.16)", color: "#a5b4fc", border: "1px solid rgba(129,140,248,0.3)" }}
                >
                  TE ACOMPAÑAMOS DE PRINCIPIO A FIN
                </span>
                <h2 className="display-5 text-white mb-3 fw-bold">
                  Te ayudamos a certificar tu empresa ante la DGII
                </h2>
                <p className="fs-lg text-light opacity-70 mb-4" style={{ maxWidth: 570 }}>
                  No tienes que recorrer el proceso solo. Nuestro equipo te orienta en cada etapa para que tu empresa pueda convertirse en emisor electrónico con claridad y confianza.
                </p>

                <div className="d-flex flex-column gap-3 mb-4">
                  {[
                    {
                      icon: "bx-key",
                      title: "Compra del certificado digital",
                      desc: "Te orientamos para elegir, adquirir y preparar el certificado digital que necesitarás para firmar tus e-CF.",
                    },
                    {
                      icon: "bx-edit-alt",
                      title: "Llenado de formularios",
                      desc: "Te asistimos con la información y los formularios requeridos durante la solicitud ante la DGII.",
                    },
                    {
                      icon: "bx-badge-check",
                      title: "Proceso de certificación",
                      desc: "Te acompañamos en las pruebas, validaciones y pasos necesarios hasta completar la certificación de tu empresa.",
                    },
                  ].map((item) => (
                    <div key={item.title} className="d-flex gap-3 align-items-start">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                        style={{ width: 46, height: 46, background: "rgba(99,102,241,0.18)", border: "1px solid rgba(129,140,248,0.22)" }}
                      >
                        <i className={`bx ${item.icon} fs-4`} style={{ color: "#a5b4fc" }} />
                      </div>
                      <div>
                        <h3 className="h6 text-white mb-1">{item.title}</h3>
                        <p className="fs-sm text-light opacity-60 mb-0">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <a
                  href="https://wa.me/18097874963?text=Hola%2C%20quiero%20ayuda%20para%20certificar%20mi%20empresa%20como%20emisor%20electr%C3%B3nico%20ante%20la%20DGII."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-lg px-4 py-3 fw-bold rounded-pill text-white"
                  style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)", boxShadow: "0 8px 26px rgba(99,102,241,0.28)" }}
                >
                  <i className="bx bxl-whatsapp me-2 fs-5" />
                  Quiero ayuda con mi certificación
                </a>
              </div>

              <div className="col-lg-6">
                <div
                  className="rounded-4 p-4 p-md-5 position-relative overflow-hidden"
                  style={{ background: "rgba(11,15,25,0.76)", border: "1px solid rgba(255,255,255,0.1)", boxShadow: "0 24px 70px rgba(0,0,0,0.24)" }}
                >
                  <div
                    className="position-absolute rounded-circle"
                    style={{ width: 180, height: 180, top: -90, right: -60, background: "rgba(139,92,246,0.18)", filter: "blur(25px)" }}
                  />
                  <div className="position-relative">
                    <div className="d-flex align-items-center justify-content-between mb-4">
                      <div>
                        <p className="text-uppercase mb-1" style={{ color: "#a5b4fc", fontSize: "0.7rem", letterSpacing: "0.1em", fontWeight: 700 }}>
                          Ruta de certificación
                        </p>
                        <h3 className="h4 text-white mb-0">Tu empresa, lista para emitir e-CF</h3>
                      </div>
                      <i className="bx bx-certification text-white" style={{ fontSize: "2.5rem", opacity: 0.2 }} />
                    </div>

                    {[
                      ["1", "Certificado digital", "Orientación para adquirirlo"],
                      ["2", "Solicitud ante la DGII", "Asistencia con el formulario"],
                      ["3", "Pruebas y validación", "Acompañamiento técnico"],
                      ["4", "Autorización", "Empresa lista para facturar"],
                    ].map(([number, title, detail], index) => (
                      <div key={title} className="d-flex gap-3 position-relative">
                        <div className="d-flex flex-column align-items-center">
                          <span
                            className="d-flex align-items-center justify-content-center rounded-circle text-white fw-bold flex-shrink-0"
                            style={{ width: 36, height: 36, background: index === 3 ? "#10b981" : "linear-gradient(135deg,#6366f1,#8b5cf6)", zIndex: 1 }}
                          >
                            {index === 3 ? <i className="bx bx-check fs-5" /> : number}
                          </span>
                          {index < 3 && <span style={{ width: 2, minHeight: 38, flex: 1, background: "rgba(129,140,248,0.22)" }} />}
                        </div>
                        <div className={index < 3 ? "pb-4" : ""}>
                          <p className="text-white fw-semibold mb-0">{title}</p>
                          <p className="fs-sm text-light opacity-50 mb-0">{detail}</p>
                        </div>
                      </div>
                    ))}

                    <div className="rounded-3 mt-4 p-3 d-flex gap-3 align-items-center" style={{ background: "rgba(16,185,129,0.09)", border: "1px solid rgba(16,185,129,0.2)" }}>
                      <i className="bx bx-support fs-3" style={{ color: "#34d399" }} />
                      <p className="text-light opacity-75 fs-sm mb-0">
                        Tendrás acompañamiento humano para entender qué sigue y avanzar en cada etapa.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Capturas del proceso de certificación DGII */}
            <div className="mt-5 pt-4" style={{ borderTop: "1px solid rgba(129,140,248,0.12)" }}>
              <div className="text-center mb-4">
                <span
                  className="badge px-3 py-2 rounded-pill mb-2 fw-bold"
                  style={{ fontSize: "0.75rem", background: "rgba(99,102,241,0.15)", color: "#a5b4fc", border: "1px solid rgba(99,102,241,0.25)" }}
                >
                  TRANSPARENCIA EN CADA PASO
                </span>
                <h3 className="h4 text-white fw-bold mb-2">Así visualizas tu avance dentro de ProVenta</h3>
                <p className="fs-sm text-light opacity-60 mb-0" style={{ maxWidth: 560, margin: "0 auto" }}>
                  Monitorea el progreso de las pruebas y la aprobación final emitida por la DGII directamente desde tu plataforma.
                </p>
              </div>

              <div className="row g-4 justify-content-center">
                {/* Screenshot 1: En progreso */}
                <div className="col-md-6 col-lg-5">
                  <div
                    className="rounded-4 p-3 p-sm-4 h-100 d-flex flex-column"
                    style={{
                      background: "rgba(11,15,25,0.72)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      boxShadow: "0 18px 45px rgba(0,0,0,0.25)",
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-3 px-1">
                      <div className="d-flex align-items-center gap-2">
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: "#fbbf24",
                            boxShadow: "0 0 8px #fbbf24",
                            display: "inline-block",
                          }}
                        />
                        <span className="text-white fw-semibold fs-sm">1. Certificación en progreso</span>
                      </div>
                      <span
                        className="badge px-2 py-1 rounded-pill"
                        style={{ background: "rgba(251,191,36,0.15)", color: "#fbbf24", border: "1px solid rgba(251,191,36,0.3)", fontSize: "0.68rem" }}
                      >
                        Pruebas activas
                      </span>
                    </div>

                    <div
                      className="rounded-3 overflow-hidden position-relative flex-grow-1 d-flex align-items-center justify-content-center"
                      style={{ background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.06)", padding: "8px" }}
                    >
                      <img
                        src="/assets/img/encf/certificacion_en_progreso.png"
                        alt="Proceso de certificación de facturación electrónica en progreso ante la DGII"
                        className="img-fluid rounded"
                        loading="lazy"
                        style={{ maxHeight: 420, width: "auto", objectFit: "contain" }}
                      />
                    </div>
                    <p className="fs-xs text-light opacity-50 mt-3 mb-0 px-1 text-center">
                      Vista en tiempo real del conjunto de pruebas y validaciones que realizamos ante el ambiente de certificación.
                    </p>
                  </div>
                </div>

                {/* Screenshot 2: Aprobada */}
                <div className="col-md-6 col-lg-5">
                  <div
                    className="rounded-4 p-3 p-sm-4 h-100 d-flex flex-column"
                    style={{
                      background: "rgba(11,15,25,0.72)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      boxShadow: "0 18px 45px rgba(0,0,0,0.25)",
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-3 px-1">
                      <div className="d-flex align-items-center gap-2">
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: "#34d399",
                            boxShadow: "0 0 8px #34d399",
                            display: "inline-block",
                          }}
                        />
                        <span className="text-white fw-semibold fs-sm">2. Certificación aprobada</span>
                      </div>
                      <span
                        className="badge px-2 py-1 rounded-pill"
                        style={{ background: "rgba(52,211,153,0.15)", color: "#34d399", border: "1px solid rgba(52,211,153,0.3)", fontSize: "0.68rem" }}
                      >
                        ✓ Aprobado DGII
                      </span>
                    </div>

                    <div
                      className="rounded-3 overflow-hidden position-relative flex-grow-1 d-flex align-items-center justify-content-center"
                      style={{ background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.06)", padding: "8px" }}
                    >
                      <img
                        src="/assets/img/encf/certificacion-31-aprobada.png"
                        alt="Certificación de factura electrónica aprobada con éxito ante la DGII"
                        className="img-fluid rounded"
                        loading="lazy"
                        style={{ maxHeight: 420, width: "auto", objectFit: "contain" }}
                      />
                    </div>
                    <p className="fs-xs text-light opacity-50 mt-3 mb-0 px-1 text-center">
                      Confirmación oficial tras superar con éxito la etapa de pruebas para emitir comprobantes e-CF válidos.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="py-5" style={{ background: "rgba(255,255,255,0.02)", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <div className="text-center mb-5">
                  <span
                    className="badge px-3 py-2 rounded-pill mb-3 fw-bold"
                    style={{ fontSize: "0.75rem", background: "rgba(14,165,233,0.15)", color: "#38bdf8", border: "1px solid rgba(14,165,233,0.25)" }}
                  >
                    PREGUNTAS FRECUENTES
                  </span>
                  <h2 className="display-5 text-white mb-2">¿Tienes dudas?</h2>
                  <p className="text-light opacity-60">Todo lo que necesitas saber sobre e-CF y ProVenta.</p>
                </div>

                <div className="accordion" id="faqEcf">
                  {faqs.map((f, i) => (
                    <div key={i} className="accordion-item border-0 rounded-4 mb-3 overflow-hidden" style={{ background: "rgba(255,255,255,0.04)" }}>
                      <h3 className="accordion-header" id={`faq-h-${i}`}>
                        <button
                          className="accordion-button shadow-none text-white fw-semibold collapsed"
                          style={{ background: "transparent" }}
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target={`#faq-b-${i}`}
                          aria-expanded="false"
                          aria-controls={`faq-b-${i}`}
                        >
                          {f.q}
                        </button>
                      </h3>
                      <div id={`faq-b-${i}`} className="accordion-collapse collapse" aria-labelledby={`faq-h-${i}`} data-bs-parent="#faqEcf">
                        <div className="accordion-body text-light opacity-70 pt-0">{f.a}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA FINAL ── */}
        <section
          className="py-5 mt-auto"
          style={{ background: "linear-gradient(135deg,#4f46e5 0%,#7c3aed 100%)" }}
        >
          <div className="container text-center py-3">
            <div
              className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
              style={{ width: 72, height: 72, background: "rgba(255,255,255,0.15)", backdropFilter: "blur(10px)" }}
            >
              <i className="bx bx-receipt text-white" style={{ fontSize: "2rem" }} />
            </div>
            <h2 className="display-5 text-white mb-3 fw-bold">
              ¿Listo para cumplir con la DGII?
            </h2>
            <p className="fs-lg text-white opacity-80 mb-5" style={{ maxWidth: 520, margin: "0 auto" }}>
              Activa la Facturación Electrónica hoy mismo. Incluida en tu plan, sin costos extra, sin trámites complejos.
            </p>
            <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
              <a
                href="/"
                onClick={(e) => e.preventDefault()}
                className="btn btn-light btn-lg px-5 py-3 fw-bold rounded-pill shadow"
                style={{ color: "#4f46e5" }}
              >
                <i className="bx bx-rocket me-2 fs-5" />
                Activar e-CF en mi cuenta
              </a>
              <a
                href="https://wa.me/18097874963"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-lg btn-outline-light border-opacity-40 px-4 py-3 rounded-pill"
              >
                <i className="bx bxl-whatsapp me-2 fs-5" />
                Hablar con soporte
              </a>
            </div>
            <p className="text-white opacity-60 mt-4 mb-0 small">
              ¿No tienes ProVenta aún?{" "}
              <Link to="/" className="text-white fw-bold text-decoration-underline">
                Prueba gratis 15 días →
              </Link>
            </p>
          </div>
        </section>

        <Footer />
      </div>
      <WhatsAppButton />
      <ShareButton />
    </React.Fragment>
  );
};

export default FacturacionElectronica;
